/*
 * kp-pdp-motion.js — the one control on the "Kyperus in motion" film.
 *
 * Nothing here touches Dawn: the section is kp-owned presentation, and the
 * film is a plain <video>. What the element does is small and deliberate:
 *
 *   - autoplay is opted into from JS, never from the `autoplay` attribute,
 *     so `prefers-reduced-motion` can veto it before a frame is decoded;
 *   - an IntersectionObserver pauses the film once it is off screen, because
 *     a looping video below the fold is pure battery;
 *   - `aria-pressed` is the single source of truth for the button, its
 *     glyph and its label, so there is no second state to drift.
 *
 * Wrapped in an IIFE: every kp-*.js is a classic <script src>, and a section
 * with a preset can put the same file on a page twice (or share a page with
 * another kp file that names the same top-level const). A bare lexical
 * declaration then throws `already declared` and kills the whole file;
 * inside a function scope it is harmless, and customElements.get() keeps
 * the define idempotent. (Ticket #22.)
 */
(() => {
const KP_MOTION_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class KpMotionPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-kp-motion-toggle]');
    this.label = this.querySelector('[data-kp-motion-label]');
    if (!this.video || !this.toggle) return;

    // The customer's own play/pause beats the observer: someone who paused a
    // film and scrolled back to it should find it still paused.
    this.pausedByHand = true;

    this.toggle.addEventListener('click', this.onToggle.bind(this));
    this.video.addEventListener('play', this.sync.bind(this));
    this.video.addEventListener('pause', this.sync.bind(this));

    /* Touch has no hover, which is the whole reason the pause mark used to
     * sit on the film for the length of the page. A tap on the film brings it
     * back for a few seconds and does nothing else: a tap that toggled
     * playback would turn the film into a mine, and the button under the
     * finger is what does the toggling.
     *
     * pointerup, not click: the film is inside the PDP's swipe and scroll
     * surfaces, and this must not fire on a drag that merely ended here. */
    this.addEventListener('pointerup', (event) => {
      if (event.target.closest('[data-kp-motion-toggle]')) return;
      this.reveal();
    });

    if (!this.hasAttribute('data-autoplay') || KP_MOTION_REDUCED.matches) return;

    this.pausedByHand = false;
    this.watch();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.revealTimer);
  }

  /* Long enough to read the mark and aim at it, short enough that the film is
   * not permanently wearing a button. A second tap restarts the clock rather
   * than stacking timers. */
  reveal() {
    this.classList.add('is-revealed');
    clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(() => this.classList.remove('is-revealed'), 2600);
  }

  watch() {
    // One screen ahead (#43): the film's header comes in before the well is
    // seen, so the play below has something to start from. Once.
    const near = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (this.video.preload === 'none') this.video.preload = 'metadata';
          near.disconnect();
        });
      },
      { rootMargin: '100% 0px' }
    );
    near.observe(this);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.pausedByHand) this.play();
          } else if (!this.video.paused) {
            this.video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    this.observer.observe(this);
  }

  onToggle() {
    if (this.video.paused) {
      this.pausedByHand = false;
      this.play();
    } else {
      this.pausedByHand = true;
      this.video.pause();
    }
  }

  play() {
    // Muted autoplay can still be refused — a low-power mode, a browser
    // setting. A rejected promise is not an error worth surfacing: the poster
    // stays and the play button is already the way back in.
    this.video.play().catch(() => {});
  }

  sync() {
    const playing = !this.video.paused;
    // A paused film shows the mark on its own, so the timed reveal has
    // nothing left to do — and must not still be running when play resumes.
    if (!playing) {
      clearTimeout(this.revealTimer);
      this.classList.remove('is-revealed');
    }
    this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    if (!this.label) return;
    this.label.textContent = playing
      ? this.toggle.dataset.pauseLabel || ''
      : this.toggle.dataset.playLabel || '';
  }
}

if (!customElements.get('kp-motion-player')) {
  customElements.define('kp-motion-player', KpMotionPlayer);
}
})();
